import { ColumnType } from '@datachain/ui-sdk/components/dc-types';
import { castDraft, immerable, produce } from 'immer';
import { Set } from 'immutable';

import { SyncWithSnapshotStatus } from './subscription.types';

export enum ColumnErrorTypes {
	MAX_LENGTH_DESC = 'max-length-desc',
	DUPLICATED = 'duplicated',
	EMPTY = 'empty',
	NOT_NORMALIZE = 'not-normalize',
}

export type ColumnReferrer = { uuid: string; label: string };

export type ColumnReferences = {
	total: number;
	profiles: Array<ColumnReferrer>;
	columnModels: Array<ColumnReferrer>;
	filterModels: Array<ColumnReferrer>;
};

interface IColumnConfigItemEntity {
	id: string;
	label: string;
	isSelectedInSource: boolean;
	isActive: boolean;
	alias: string;
	description: string;
	columnType: ColumnType;
	isList: boolean;
	isDeleted: boolean;
	isHashed: boolean;
	syncStatus: SyncWithSnapshotStatus;
	referencedBy: ColumnReferences;
	errors: Set<ColumnErrorTypes>;
}

const entityDefaults: IColumnConfigItemEntity = {
	id: '-1',
	label: '',
	isSelectedInSource: false,
	isActive: false,
	alias: '',
	description: '',
	columnType: ColumnType.Unknown,
	isList: false,
	isDeleted: false,
	isHashed: false,
	syncStatus: 'N/A',
	referencedBy: {
		total: 0,
		profiles: [],
		filterModels: [],
		columnModels: [],
	},
	errors: Set(),
};

export class ColumnConfigItemEntity implements IColumnConfigItemEntity {
	private [immerable] = true;
	public id = entityDefaults.id;
	public label = entityDefaults.label;
	public isSelectedInSource = entityDefaults.isSelectedInSource;
	public isActive = entityDefaults.isActive;
	public alias = entityDefaults.alias;
	public description = entityDefaults.description;
	public columnType = entityDefaults.columnType;
	public isList = entityDefaults.isList;
	public isDeleted = entityDefaults.isDeleted;
	public isHashed = entityDefaults.isHashed;
	public syncStatus = entityDefaults.syncStatus;
	public referencedBy = entityDefaults.referencedBy;
	public errors = entityDefaults.errors;

	public static build(
		params: Partial<IColumnConfigItemEntity> = {}
	): ColumnConfigItemEntity {
		const instance = new ColumnConfigItemEntity();
		instance.id = params.id ?? entityDefaults.id;
		instance.label = params.label ?? entityDefaults.label;
		instance.isSelectedInSource =
			params.isSelectedInSource ?? entityDefaults.isSelectedInSource;
		instance.isActive = params.isActive ?? entityDefaults.isActive;
		instance.alias = params.alias ?? entityDefaults.alias;
		instance.description = params.description ?? entityDefaults.description;
		instance.columnType = params.columnType ?? entityDefaults.columnType;
		instance.isList = params.isList ?? entityDefaults.isList;
		instance.isDeleted = params.isDeleted ?? entityDefaults.isDeleted;
		instance.isHashed = params.isHashed ?? entityDefaults.isHashed;
		instance.syncStatus = params.syncStatus ?? entityDefaults.syncStatus;
		instance.referencedBy = params.referencedBy ?? entityDefaults.referencedBy;
		instance.errors = params.errors ?? entityDefaults.errors;

		return instance;
	}

	public setIsActive(isActive: boolean): ColumnConfigItemEntity {
		return produce(this, (draft) => {
			draft.isActive = isActive;
			draft.isHashed = !isActive ? false : draft.isHashed;
		});
	}

	public setIsSelectedInSource(
		isSelectedInSource: boolean
	): ColumnConfigItemEntity {
		return produce(this, (draft) => {
			draft.isSelectedInSource = isSelectedInSource;
		});
	}

	public setAlias(alias: string): ColumnConfigItemEntity {
		return produce(this, (draft) => {
			draft.alias = castDraft(alias);
		});
	}

	public setDescription(description: string): ColumnConfigItemEntity {
		return produce(this, (draft) => {
			draft.description = castDraft(description);
		});
	}

	public setIsHashed(isHashed: boolean): ColumnConfigItemEntity {
		return produce(this, (draft) => {
			draft.isHashed = isHashed;
		});
	}

	public setErrors(errors: Set<ColumnErrorTypes>): ColumnConfigItemEntity {
		return produce(this, (draft) => {
			draft.errors = castDraft(errors);
		});
	}

	public isEqualTo(columnItem: ColumnConfigItemEntity): boolean {
		return this.id === columnItem.id;
	}
}
